/* eslint-disable @typescript-eslint/no-unused-vars */
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs))
}

export function isMobileDevice(): boolean {
    // Check for mobile user agents
    const mobileRegex =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i
    const userAgent = navigator.userAgent

    // Check for touch device and screen size
    const isTouchDevice =
        'ontouchstart' in window || navigator.maxTouchPoints > 0
    const isSmallScreen = window.innerWidth <= 768

    return mobileRegex.test(userAgent) || (isTouchDevice && isSmallScreen)
}

export function sleep(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms))
}

export function formatCurrency(amount: number, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
    }).format(amount)
}

export function generateId() {
    return Math.random().toString(36).substring(2) + Date.now().toString(36)
}

export function camelCaseToNormalCase(text: string): string {
    if (!text) {
        return '' // Handle empty or null input
    }

    const result = text.replace(/([A-Z])/g, ' $1') // Add spaces before capital letters
    return result.charAt(0).toUpperCase() + result.slice(1).trim() // Capitalize first letter and trim extra spaces
}

// Define the type for the category colors
// This is a mapping of category names to their respective text and background colors in
type CategoryColors = {
    [key: string]: { text: string; bg: string }
}

export const categoryTagColors = (category: string) => {
    const colors: CategoryColors = {
        'customer satisfaction': {
            text: '#00527C',
            bg: '#e0f0ff',
        },
        'product research': {
            text: '#114a30',
            bg: '#e8fff1',
        },
        'customer loyalty': {
            text: '#675c00',
            bg: '#fff7cb',
        },
        'market analysis': {
            text: '#883117',
            bg: '#ffeeee',
        },
    }

    return (
        colors[category?.toLocaleLowerCase()] || { text: '#000', bg: '#f3f4f6' }
    )
}

type NPSCondition = {
    operator: string
    min?: number
    max?: number
    value?: number | string
}

type EvaluationResult = {
    success: boolean
    error?: string
}

/**
 * Evaluate a condition based on the answer provided
 * @param answer
 * @param condition
 * @returns
 * @example
 * evaluateCondition(5, { operator: ">", value: 3 }) // true
 * evaluateCondition(5, { operator: "<", value: 3 }) // false
 * evaluateCondition(7, { operator: "between", min: 5, max: 10 }) // true
 * evaluateCondition(3, { operator: "between", min: 5, max: 10 }) // false
 */
export const evaluateCondition = (
    answer: number | string | string[],
    condition: NPSCondition
): EvaluationResult => {
    // Convert answer to number if it's a numeric string
    const numericAnswer =
        typeof answer === 'string' && !isNaN(Number(answer))
            ? Number(answer)
            : answer

    try {
        switch (condition.operator.toLowerCase()) {
            case 'between':
                return handleBetweenCondition(numericAnswer, condition)

            case '<':
            case 'less_than':
                return handleLessThan(numericAnswer, condition)

            case '>':
            case 'greater_than':
                return handleGreaterThan(numericAnswer, condition)

            case '<=':
            case 'less_than_or_equal':
                return handleLessThanOrEqual(numericAnswer, condition)

            case '>=':
            case 'greater_than_or_equal':
                return handleGreaterThanOrEqual(numericAnswer, condition)

            default:
                return {
                    success: false,
                    error: `Unsupported operator: ${condition.operator}`,
                }
        }
    } catch (error) {
        return {
            success: false,
            error:
                error instanceof Error
                    ? error.message
                    : 'Unknown error occurred',
        }
    }
}

// Helper function: Between condition
const handleBetweenCondition = (
    answer: number | string | string[],
    condition: NPSCondition
): EvaluationResult => {
    if (
        typeof condition.min === 'undefined' ||
        typeof condition.max === 'undefined'
    ) {
        throw new Error('Between condition requires both min and max values')
    }

    if (typeof answer !== 'number') {
        return {
            success: false,
            error: 'Between condition requires numeric answer',
        }
    }

    return {
        success: answer >= condition.min && answer <= condition.max,
    }
}

// Helper function: Less than condition
const handleLessThan = (
    answer: number | string | string[],
    condition: NPSCondition
): EvaluationResult => {
    if (typeof condition.value === 'undefined') {
        throw new Error('Less than condition requires a value')
    }

    const comparisonValue =
        typeof condition.value === 'string'
            ? parseFloat(condition.value)
            : condition.value

    if (typeof answer !== 'number') {
        return {
            success: false,
            error: 'Less than condition requires numeric answer',
        }
    }

    return {
        success: answer < comparisonValue,
    }
}

// Helper function: Greater than condition
const handleGreaterThan = (
    answer: number | string | string[],
    condition: NPSCondition
): EvaluationResult => {
    if (typeof condition.value === 'undefined') {
        throw new Error('Greater than condition requires a value')
    }

    const comparisonValue =
        typeof condition.value === 'string'
            ? parseFloat(condition.value)
            : condition.value

    if (typeof answer !== 'number') {
        return {
            success: false,
            error: 'Greater than condition requires numeric answer',
        }
    }

    return {
        success: answer > comparisonValue,
    }
}

// Helper function: Less than or equal condition
const handleLessThanOrEqual = (
    answer: number | string | string[],
    condition: NPSCondition
): EvaluationResult => {
    if (typeof condition.value === 'undefined') {
        throw new Error('Less than or equal condition requires a value')
    }

    const comparisonValue =
        typeof condition.value === 'string'
            ? parseFloat(condition.value)
            : condition.value

    if (typeof answer !== 'number') {
        return {
            success: false,
            error: 'Less than or equal condition requires numeric answer',
        }
    }

    return {
        success: answer <= comparisonValue,
    }
}

// Helper function: Greater than or equal condition
const handleGreaterThanOrEqual = (
    answer: number | string | string[],
    condition: NPSCondition
): EvaluationResult => {
    if (typeof condition.value === 'undefined') {
        throw new Error('Greater than or equal condition requires a value')
    }

    const comparisonValue =
        typeof condition.value === 'string'
            ? parseFloat(condition.value)
            : condition.value

    if (typeof answer !== 'number') {
        return {
            success: false,
            error: 'Greater than or equal condition requires numeric answer',
        }
    }

    return {
        success: answer >= comparisonValue,
    }
}

/**
 * Get the color for the badge based on the status
 * @param status - The status of the template
 * @returns  The color for the badge
 */
export const getBadgeColor = (status: string | undefined) => {
    switch (status?.toLocaleLowerCase()) {
        case 'active':
            return 'bg-green-500'
        case 'draft':
            return 'bg-gray-500'
        case 'paused':
            return 'bg-yellow-500'
        case 'archived':
            return 'bg-red-500'
        default:
            return 'bg-gray-500'
    }
}

/**
 * Validate an image based on the specified criteria
 * @param file - The image file to validate
 * @param onError - Callback function to handle errors
 * @param MAX_FILE_SIZE : Maximum file size in bytes (e.g., 200 * 1024 for 200KB)
 * @param MIN_WIDTH : Minimum width of the image in pixels (e.g., 512)
 * @param ALLOWED_TYPES : Array of allowed file types ["image/jpeg", "image/png", "image/webp", "image/heic"]
 * @param thingsToCheck : Array of things to check "fileType" | "fileSize" | "imageDimensions" | "imageHeight"
 * @returns A promise that resolves to a boolean indicating if the image is valid
 * @example validateImage(file, onError, 200 * 1024, 512, ["image/jpeg", "image/png"], ["fileType", "fileSize", "imageDimensions"])
 */
export const validateImage = async (
    file: File,
    onError: (message: string) => void,
    MAX_FILE_SIZE: number,
    MIN_WIDTH: number,
    ALLOWED_TYPES: string[],
    thingsToCheck: string[]
): Promise<boolean> => {
    let isValid = true

    // Check file type
    if (
        thingsToCheck.includes('fileType') &&
        !ALLOWED_TYPES.includes(file.type)
    ) {
        onError('Invalid file type. Please upload HEIC, WEBP, PNG, or JPG')
        isValid = false
    }

    // Check file size
    if (thingsToCheck.includes('fileSize') && file.size > MAX_FILE_SIZE) {
        onError('File size must be less than 200KB')
        isValid = false
    }

    // Check image dimensions
    if (thingsToCheck.includes('imageDimensions')) {
        try {
            const image = await createImageBitmap(file)
            if (image.width < MIN_WIDTH) {
                onError('Image width must be at least 512 pixels')
                isValid = false
            }
            // You can add more dimension checks here if needed
        } catch (error) {
            onError('Error validating image dimensions')
            isValid = false
        }
    }

    // Add more checks as needed
    // For example, you can add a check for image height
    if (thingsToCheck.includes('imageHeight')) {
        try {
            const image = await createImageBitmap(file)
            if (image.height < MIN_WIDTH) {
                // Assuming MIN_WIDTH is used as a placeholder for minimum height
                onError('Image height must be at least 512 pixels')
                isValid = false
            }
        } catch (error) {
            onError('Error validating image height')
            isValid = false
        }
    }

    return isValid
}

/**
 * Returns the currency symbol for a given currency code.
 * Falls back to currency code if symbol can't be derived.
 */
export const getCurrencySymbol = (currencyCode: string): string => {
    try {
        const formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currencyCode,
            currencyDisplay: 'symbol',
        })
        const parts = formatter.formatToParts(0)
        const symbolPart = parts.find((part) => part.type === 'currency')
        return symbolPart?.value || currencyCode
    } catch (e) {
        console.warn(`Invalid or unsupported currency code: ${currencyCode}`)
        return currencyCode
    }
}

/**
 * Downloads a file from a URL
 * @param url - The URL of the file to download
 * @param filename - The name for the downloaded file
 */
export const downloadFile = (url: string, filename: string): void => {
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

/**
 * Downloads an image from a URL by converting it to a blob
 * @param imageUrl - The URL of the image to download
 * @param filename - The name for the downloaded file
 */
export const downloadImage = async (
    imageUrl: string,
    filename: string
): Promise<void> => {
    try {
        const response = await fetch(imageUrl)
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        downloadFile(url, filename)
        window.URL.revokeObjectURL(url)
    } catch (error) {
        console.error('Error downloading image:', error)
    }
}

export const formatTargetRules = (
    targetRules: Array<{
        type: string
        value?: string
        values?: string[]
        labels?: string[]
        operator?: string
        maxValue?: string
    }>,
    currencyCode: string = 'USD'
) => {
    if (!targetRules || targetRules.length === 0) {
        return 'All customers'
    }

    const parts = []
    const PAYMENT_METHOD_LABELS: Record<string, string> = {
        creditCard: 'Credit Card',
        deferred: 'Deferred',
        local: 'Local',
        manualPayment: 'Manual Payment',
        offsite: 'Offsite',
        paymentOnDelivery: 'Payment on Delivery',
        redeemable: 'Redeemable',
        wallet: 'Wallet',
        customOnsite: 'Custom Onsite',
        other: 'Other',
    }

    // Handle customer type first
    const customerTypeRule = targetRules.find(
        (rule) => rule.type === 'customerType'
    )
    if (customerTypeRule) {
        if (customerTypeRule.value === 'all') {
            parts.push('All customers')
        } else if (customerTypeRule.value === 'firstTime') {
            parts.push('New customers')
        } else if (customerTypeRule.value === 'repeat') {
            parts.push('Repeat customers')
        }
    } else {
        parts.push('All customers')
    }
    // Handle order value
    const orderValueRule = targetRules.find(
        (rule) => rule.type === 'orderValue'
    )
    if (orderValueRule) {
        const currencySymbol = getCurrencySymbol(currencyCode)
        if (orderValueRule.operator === 'between') {
            parts.push(
                `with order value between ${currencySymbol}${orderValueRule.value} and ${currencySymbol}${orderValueRule.maxValue}`
            )
        } else if (orderValueRule.operator === 'equals') {
            parts.push(
                `with order value equal to ${currencySymbol}${orderValueRule.value}`
            )
        } else if (orderValueRule.operator === 'greater_than') {
            parts.push(
                `with order value greater than ${currencySymbol}${orderValueRule.value}`
            )
        } else if (orderValueRule.operator === 'less_than') {
            parts.push(
                `with order value less than ${currencySymbol}${orderValueRule.value}`
            )
        }
    }

    // Handle customer tags
    const customerTagsIncluded = targetRules
        .filter(
            (rule) =>
                rule.type === 'customerTags' && rule.operator === 'contains'
        )
        .flatMap((rule) => rule.values || [])
    const customerTagsExcluded = targetRules
        .filter(
            (rule) =>
                rule.type === 'customerTags' &&
                rule.operator === 'does_not_contains'
        )
        .flatMap((rule) => rule.values || [])

    if (customerTagsIncluded.length > 0) {
        parts.push(`with customer tags: ${customerTagsIncluded.join(', ')}`)
    }
    if (customerTagsExcluded.length > 0) {
        parts.push(
            `excluding customer tags: ${customerTagsExcluded.join(', ')}`
        )
    }

    // Handle payment methods
    const paymentMethodsIncluded = targetRules
        .filter(
            (rule) =>
                rule.type === 'paymentMethods' && rule.operator === 'contains'
        )
        .flatMap((rule) => rule.values || [])
        .map((value) => PAYMENT_METHOD_LABELS[value] || value)
    const paymentMethodsExcluded = targetRules
        .filter(
            (rule) =>
                rule.type === 'paymentMethods' &&
                rule.operator === 'does_not_contains'
        )
        .flatMap((rule) => rule.values || [])
        .map((value) => PAYMENT_METHOD_LABELS[value] || value)

    if (paymentMethodsIncluded.length > 0) {
        parts.push(
            `using payment methods: ${paymentMethodsIncluded.join(', ')}`
        )
    }
    if (paymentMethodsExcluded.length > 0) {
        parts.push(
            `excluding payment methods: ${paymentMethodsExcluded.join(', ')}`
        )
    }

    // Handle gift card
    const giftCardRule = targetRules.find((rule) => rule.type === 'giftCard')
    if (giftCardRule) {
        const hasGiftCard =
            giftCardRule.value === 'true' || giftCardRule.value === 'yes'
        if (giftCardRule.operator === 'is') {
            parts.push(
                hasGiftCard
                    ? 'with gift card applied'
                    : 'without gift card applied'
            )
        }
    }

    // Handle discount codes
    const discountCodesIncluded = targetRules
        .filter(
            (rule) =>
                rule.type === 'discountCodes' && rule.operator === 'contains'
        )
        .flatMap((rule) => rule.values || [])
    const discountCodesExcluded = targetRules
        .filter(
            (rule) =>
                rule.type === 'discountCodes' &&
                rule.operator === 'does_not_contains'
        )
        .flatMap((rule) => rule.values || [])

    if (discountCodesIncluded.length > 0) {
        parts.push(`with discount codes: ${discountCodesIncluded.join(', ')}`)
    }
    if (discountCodesExcluded.length > 0) {
        parts.push(
            `excluding discount codes: ${discountCodesExcluded.join(', ')}`
        )
    }

    // Handle zip codes
    const zipCodesIncluded = targetRules
        .filter(
            (rule) => rule.type === 'zipCode' && rule.operator === 'contains'
        )
        .flatMap((rule) => rule.values || [])
    const zipCodesExcluded = targetRules
        .filter(
            (rule) =>
                rule.type === 'zipCode' && rule.operator === 'does_not_contains'
        )
        .flatMap((rule) => rule.values || [])

    if (zipCodesIncluded.length > 0) {
        parts.push(`in zip codes: ${zipCodesIncluded.join(', ')}`)
    }
    if (zipCodesExcluded.length > 0) {
        parts.push(`excluding zip codes: ${zipCodesExcluded.join(', ')}`)
    }

    // Handle locations
    const locationsIncluded = targetRules
        .filter(
            (rule) => rule.type === 'location' && rule.operator === 'contains'
        )
        .flatMap((rule) => rule.values || [])
        .map((loc) => {
            const [, value] = loc.split(':')
            return value
        })
    const locationsExcluded = targetRules
        .filter(
            (rule) =>
                rule.type === 'location' &&
                rule.operator === 'does_not_contains'
        )
        .flatMap((rule) => rule.values || [])
        .map((loc) => {
            const [, value] = loc.split(':')
            return value
        })

    if (locationsIncluded.length > 0) {
        parts.push(`in ${locationsIncluded.join(', ')}`)
    }
    if (locationsExcluded.length > 0) {
        parts.push(`excluding ${locationsExcluded.join(', ')}`)
    }

    // Handle order quantity
    const orderQuantityRule = targetRules.find(
        (rule) => rule.type === 'orderQuantity'
    )
    if (orderQuantityRule) {
        if (orderQuantityRule.operator === 'equals') {
            parts.push(`with ${orderQuantityRule.value} lifetime orders`)
        } else if (orderQuantityRule.operator === 'greater_than') {
            parts.push(
                `with more than ${orderQuantityRule.value} lifetime orders`
            )
        } else if (orderQuantityRule.operator === 'less_than') {
            parts.push(
                `with less than ${orderQuantityRule.value} lifetime orders`
            )
        } else if (orderQuantityRule.operator === 'between') {
            parts.push(
                `with ${orderQuantityRule.value} to ${orderQuantityRule.maxValue} lifetime orders`
            )
        }
    }

    // Handle product IDs
    const productIdsIncluded = targetRules.filter(
        (rule) => rule.type === 'productIds' && rule.operator === 'contains'
    )
    const productIdsExcluded = targetRules.filter(
        (rule) =>
            rule.type === 'productIds' && rule.operator === 'does_not_contains'
    )

    if (productIdsIncluded.length > 0) {
        const productLabels = productIdsIncluded.flatMap(
            (rule) => rule.labels || []
        )
        parts.push(`who purchased: ${productLabels.join(', ')}`)
    }
    if (productIdsExcluded.length > 0) {
        const productLabels = productIdsExcluded.flatMap(
            (rule) => rule.labels || []
        )
        parts.push(`who did not purchase: ${productLabels.join(', ')}`)
    }

    // Handle collection IDs
    const collectionIdsIncluded = targetRules.filter(
        (rule) => rule.type === 'collectionIds' && rule.operator === 'contains'
    )
    const collectionIdsExcluded = targetRules.filter(
        (rule) =>
            rule.type === 'collectionIds' &&
            rule.operator === 'does_not_contains'
    )

    if (collectionIdsIncluded.length > 0) {
        const collectionLabels = collectionIdsIncluded.flatMap(
            (rule) => rule.labels || []
        )
        parts.push(`from collections: ${collectionLabels.join(', ')}`)
    }
    if (collectionIdsExcluded.length > 0) {
        const collectionLabels = collectionIdsExcluded.flatMap(
            (rule) => rule.labels || []
        )
        parts.push(`excluding collections: ${collectionLabels.join(', ')}`)
    }

    // Handle line items count
    const lineItemsCountRule = targetRules.find(
        (rule) => rule.type === 'lineItemsCount'
    )
    if (lineItemsCountRule) {
        if (lineItemsCountRule.operator === 'equals') {
            parts.push(`with exactly ${lineItemsCountRule.value} line items`)
        } else if (lineItemsCountRule.operator === 'greater_than') {
            parts.push(`with more than ${lineItemsCountRule.value} line items`)
        } else if (lineItemsCountRule.operator === 'less_than') {
            parts.push(`with less than ${lineItemsCountRule.value} line items`)
        } else if (lineItemsCountRule.operator === 'between') {
            parts.push(
                `with ${lineItemsCountRule.value} to ${lineItemsCountRule.maxValue} No. of order items`
            )
        }
    }

    // Handle variant title
    const variantTitleIncluded = targetRules
        .filter(
            (rule) =>
                rule.type === 'variantTitle' && rule.operator === 'contains'
        )
        .flatMap((rule) => rule.values || [])
    const variantTitleExcluded = targetRules
        .filter(
            (rule) =>
                rule.type === 'variantTitle' &&
                rule.operator === 'does_not_contains'
        )
        .flatMap((rule) => rule.values || [])

    if (variantTitleIncluded.length > 0) {
        parts.push(`with variant titles: ${variantTitleIncluded.join(', ')}`)
    }
    if (variantTitleExcluded.length > 0) {
        parts.push(
            `excluding variant titles: ${variantTitleExcluded.join(', ')}`
        )
    }

    return parts.join(' ')
}
